<template>
  <div class="tw:flex tw:flex-col tw:gap-y-6">
    <i18n-t v-if="defaultPaymentMethod?.expired || cardExpiresSoon" keypath="rc_fop_expiring" tag="p" scope="global">
      <template #Campaign>
        <b>{{ $t(campaignTypes[campaignType]) }}</b>
      </template>
    </i18n-t>

    <i18n-t v-else-if="!defaultPaymentMethod?.expired" keypath="rc_fop_confirmation" tag="p" scope="global">
      <template #Campaign>
        <b>{{ $t(campaignTypes[campaignType]) }}</b>
      </template>
    </i18n-t>

    <!-- Successfully updated notification -->
    <div
      v-if="!defaultPaymentMethod?.expired && isPaymentUpdated"
      class="tw:bg-green-200 tw:text-sm tw:rounded-lg tw:px-5 tw:py-4"
    >
      {{ $t('paymentMethods.edit.success') }}
    </div>

    <!-- Card expired notification -->
    <CardExpiredInfo
      v-if="defaultPaymentMethod?.expired && !isPaymentUpdated"
      type="RATE_CONNECT"
      :defaultPaymentMethod="defaultPaymentMethod"
    />

    <!-- Card soon to expire notification -->
    <CardToExpireInfo
      v-else-if="cardExpiresSoon && !isPaymentUpdated"
      :cardExpirationDate="cardExpirationDate"
      :defaultPaymentMethod="defaultPaymentMethod"
      type="RATE_CONNECT"
    />

    <!-- Wire transfer -->
    <WireTransferInfo
      v-if="defaultPaymentMethod?.type === 'WIRE_TRANSFER'"
      type="RATE_CONNECT"
      :defaultPaymentMethod="defaultPaymentMethod"
    />

    <DefaultPaymentMethodCard
      :defaultPaymentMethod="defaultPaymentMethod"
      @openPaymentPage="openPaymentPage"
      :cardExpiresSoon="cardExpiresSoon"
      type="RATE_CONNECT"
    />

    <i18n-t keypath="rc_resume_terms_message" tag="p" class="tw:text-xs" v-if="!defaultPaymentMethod?.expired">
      <template #RCTerms>
        <BaseLink
          href="/home/terms-and-conditions?app=rate-connect"
          target="_blank"
          class="tw:cursor-pointer tw:underline tw:text-[revert] tw:text-gray-800 tw:hover:text-blue-800"
        >
          {{ $t('rc_terms_conditions') }}
        </BaseLink>
      </template>
    </i18n-t>
  </div>
</template>
<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import BaseLink from '@/components/BaseLink.vue'
import DefaultPaymentMethodCard from '@/components/rateConnect/resumeFlow/DefaultPaymentMethodCard.vue'
import CardToExpireInfo from './CardToExpireInfo.vue'
import CardExpiredInfo from './CardExpiredInfo.vue'
import WireTransferInfo from './WireTransferInfo.vue'

import { selectedAccommodationId } from '@/layouts/queries'
import { useCampaign } from '@/components/rateConnect/queries'
import { useRateConnect } from '@/components/rateConnect/useRateConnect'

const { toggleResumeCampaign } = useRateConnect()

const router = useRouter()
const props = defineProps({
  defaultPaymentMethod: Object,
})

const openPaymentPage = () => {
  router.push({
    name: 'billing-edit-payment',
    query: { isResumeFlow: true, sourceRoute: router.currentRoute.value.name, productType: 'RATE_CONNECT' },
  })
  toggleResumeCampaign('close')
}

const { campaignsData } = useCampaign(selectedAccommodationId)
const campaignType = computed(() => campaignsData.value?.campaignType)
const campaignTypes = { netCpa: 'pay_per_stay', cpc: 'pay_per_click', cpa: 'rc_pay_per_booking' }
const isPaymentUpdated = computed(() => router.currentRoute.value.query?.isUpdated === 'true')
const cardExpirationDate = computed(() => {
  const expMonth = props.defaultPaymentMethod?.expirationMonth
  const expYear = props.defaultPaymentMethod?.expirationYear
  if (!expMonth || !expYear) return null

  return new Date(expYear, expMonth, 0)
})
const cardExpiresSoon = computed(() => {
  if (!cardExpirationDate.value) return false

  const today = new Date()
  const msPerDay = 24 * 60 * 60 * 1000
  const daysUntilExpiration = Math.ceil((cardExpirationDate.value.getTime() - today.getTime()) / msPerDay)
  return daysUntilExpiration < 30 && daysUntilExpiration >= 0
})
</script>
